import { useEffect, useState } from 'react';
import { useAnalytics } from './useAnalytics';

/**
 * Custom hook for A/B testing with PostHog
 * @param {string} experimentKey - The key for the experiment (e.g., 'landing_headline', 'landing_cta')
 * @param {Array} variants - Array of variant options
 * @param {Object} options - Additional options
 * @returns {Object} - { variant, loading }
 */
export const useABTest = (experimentKey, variants = [], options = {}) => {
  const { analytics } = useAnalytics();
  const [variant, setVariant] = useState(options.defaultVariant || variants[0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!analytics || !experimentKey || variants.length === 0) {
      setLoading(false);
      return;
    }

    // Get feature flag from PostHog
    const getVariant = async () => {
      try {
        // Check if PostHog is loaded
        if (!window.posthog) {
          console.warn('PostHog not loaded, using default variant');
          setLoading(false);
          return;
        }

        // Get the feature flag variant
        const flagValue = window.posthog.getFeatureFlag(experimentKey);
        
        if (flagValue && variants.includes(flagValue)) {
          setVariant(flagValue);
        } else {
          // If no flag value or invalid value, randomly assign and track
          const randomIndex = Math.floor(Math.random() * variants.length);
          const selectedVariant = variants[randomIndex];
          setVariant(selectedVariant);
          
          // Track the assignment
          analytics.trackEvent('ab_test_assignment', {
            experiment: experimentKey,
            variant: selectedVariant,
            variants_available: variants,
          });
        }

        setLoading(false);
      } catch (error) {
        console.error('Error getting A/B test variant:', error);
        setLoading(false);
      }
    };

    // Wait for PostHog to be ready
    if (window.posthog && window.posthog.isFeatureEnabled) {
      getVariant();
    } else {
      // PostHog might not be loaded yet, wait for it
      const checkInterval = setInterval(() => {
        if (window.posthog && window.posthog.isFeatureEnabled) {
          clearInterval(checkInterval);
          getVariant();
        }
      }, 100);

      // Timeout after 2 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (loading) {
          setLoading(false);
        }
      }, 2000);
    }
  }, [experimentKey, variants, analytics]);

  return { variant, loading };
};

/**
 * Hook for tracking A/B test conversions
 */
export const useABTestConversion = () => {
  const { trackEvent } = useAnalytics();

  const trackConversion = (experimentKey, variant, conversionType = 'click') => {
    trackEvent('ab_test_conversion', {
      experiment: experimentKey,
      variant,
      conversion_type: conversionType,
      timestamp: new Date().toISOString(),
    });
  };

  return { trackConversion };
};